/**
 * User Management page - Admin-only interface for managing users.
 * Displays user list with create, edit, reset password, and deactivate actions.
 */

import { FC, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Block as BlockIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { api } from '@/services/api';
import type { UserListItem, UserCreateRequest, UserUpdateRequest } from '@/types/admin';
import type { Role } from '@/types/auth';
import { UserFormModal } from '@/components/Admin/UserFormModal';
import { PasswordResetModal } from '@/components/Admin/PasswordResetModal';
import { useAuthStore } from '@/stores/authStore';

export const UserManagement: FC = () => {
  const currentUser = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);

  // Modal states
  const [userFormOpen, setUserFormOpen] = useState(false);
  const [passwordResetOpen, setPasswordResetOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.admin.listUsers();
      setUsers(response.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const rolesData = await api.admin.listRoles();
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to load roles:', err);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserFormOpen(true);
  };

  const handleEditUser = (user: UserListItem) => {
    setSelectedUser(user);
    setUserFormOpen(true);
  };

  const handleResetPassword = (user: UserListItem) => {
    setSelectedUser(user);
    setPasswordResetOpen(true);
  };

  const handleDeactivateUser = async (user: UserListItem) => {
    if (!confirm(`Are you sure you want to deactivate user "${user.username}"?`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      await api.admin.deactivateUser(user.id);
      setSuccess(`User "${user.username}" has been deactivated successfully`);
      fetchUsers(); // Refresh list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate user');
    }
  };

  const handleUserFormSubmit = async (userData: UserCreateRequest | UserUpdateRequest) => {
    try {
      setError(null);
      setSuccess(null);

      if (selectedUser) {
        // Update existing user
        await api.admin.updateUser(selectedUser.id, userData as UserUpdateRequest);
        setSuccess(`User "${selectedUser.username}" updated successfully`);
      } else {
        // Create new user
        await api.admin.createUser(userData as UserCreateRequest);
        setSuccess('User created successfully');
      }

      setUserFormOpen(false);
      fetchUsers(); // Refresh list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    }
  };

  const handlePasswordResetSubmit = async (newPassword: string, forceChange: boolean) => {
    if (!selectedUser) return;

    try {
      setError(null);
      setSuccess(null);
      await api.admin.resetPassword(selectedUser.id, {
        new_password: newPassword,
        force_change: forceChange,
      });
      setSuccess(`Password reset successfully for user "${selectedUser.username}"`);
      setPasswordResetOpen(false);
      fetchUsers(); // Refresh to update must_change_password flag
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getRoleColor = (roleName: string): 'primary' | 'secondary' | 'success' | 'warning' | 'default' => {
    switch (roleName) {
      case 'admin':
        return 'primary';
      case 'engineer':
        return 'secondary';
      case 'operator':
        return 'success';
      case 'viewer':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">User Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          Create User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {user.username}
                      {user.must_change_password && (
                        <Tooltip title="Must change password on next login">
                          <WarningIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{user.full_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {user.roles.map((role) => (
                        <Chip
                          key={role.id}
                          label={role.display_name}
                          size="small"
                          color={getRoleColor(role.name)}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={user.is_active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{formatTimestamp(user.last_login_at)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit User">
                      <IconButton
                        size="small"
                        onClick={() => handleEditUser(user)}
                        disabled={!user.is_active}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                      <IconButton
                        size="small"
                        onClick={() => handleResetPassword(user)}
                        disabled={!user.is_active}
                      >
                        <LockIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate User">
                      <IconButton
                        size="small"
                        onClick={() => handleDeactivateUser(user)}
                        disabled={!user.is_active || user.id === currentUser?.id}
                        color="error"
                      >
                        <BlockIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* User Form Modal (Create/Edit) */}
      <UserFormModal
        open={userFormOpen}
        onClose={() => setUserFormOpen(false)}
        onSubmit={handleUserFormSubmit}
        user={selectedUser}
        roles={roles}
      />

      {/* Password Reset Modal */}
      <PasswordResetModal
        open={passwordResetOpen}
        onClose={() => setPasswordResetOpen(false)}
        onSubmit={handlePasswordResetSubmit}
        username={selectedUser?.username || ''}
      />
    </Box>
  );
};
